import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import AptosNormal from "../../../../utils/Customfonts/Aptos.ttf";
import AptosBold from "../../../../utils/Customfonts/Aptos-Bold.ttf";
import moment from "moment";
import { NDT_METHOD_TYPES } from "../const/ndtMethodDefs";

const UnitechHeader = "../img/header.png";
const UnitechFooter = "../img/IR_Footer.png";

export const customFontFamily = {
  bold: "bold",
  normal: "normal",
  boldFont: "AptosBold",
  normalFont: "AptosNormal",
};

export const config = {
  margin: 15,
  borderMargin: 15,
  headerimgHeight: 70,
  footerimgHeight: 0,
  pageNoHeight: 15,
  borderGap: 2,
  fontSize: 9.5,
  fontSizeSm: 9,
  fontSizeLg: 12,
  fontSizeTitle: 15,
  cellPadding: 4,
  sectionGap: 0,
  borderFooterGap: 15,

  get contentTop() {
    return this.headerimgHeight + this.borderGap + this.borderMargin + 15;
  },
  get contentBottom() {
    return this.footerimgHeight + this.pageNoHeight + this.borderGap + this.borderMargin + 5;
  },
};

const getBaseStyles = () => ({
  fontSize: config.fontSize,
  cellPadding: config.cellPadding,
  lineColor: [0, 0, 0],
  lineWidth: 0.5,
  textColor: [0, 0, 0],
  fillColor: false,
  font: customFontFamily.normalFont,
  fontStyle: customFontFamily.normal,
  halign: "left",
  valign: "middle",
});

const getHeaderStyles = () => ({
  ...getBaseStyles(),
  font: customFontFamily.boldFont,
  fontStyle: customFontFamily.bold,
});

const getTableMargins = () => ({
  left: config.margin,
  right: config.margin,
  top: config.contentTop,
  bottom: config.contentBottom,
});

const layout = {
  doc: null,
  y: 0,
  pageWidth: 0,
  pageHeight: 0,
  contentWidth: 0,

  init(doc) {
    this.doc = doc;
    this.pageWidth = doc.internal.pageSize.getWidth();
    this.pageHeight = doc.internal.pageSize.getHeight();
    this.contentWidth = this.pageWidth - config.margin * 2;
    this.y = config.contentTop;
  },
  afterTable(gap) {
    this.y = this.doc.lastAutoTable.finalY + (gap ?? config.sectionGap);
  },
  advance(delta) {
    this.y += delta;
  },
  newPage() {
    this.doc.addPage();
    this.y = config.contentTop;
  },
};

// FONT LOADER
const arrayBufferToBase64 = (buffer) => {
  let binary = "";
  const bytes = new Uint8Array(buffer);
  const chunkSize = 8192;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
  }
  return btoa(binary);
};

const loadAndAddFont = async (pdfDoc, fontUrl, fontNameTTF, fontName, fontStyle) => {
  const fontResponse = await fetch(fontUrl);
  const fontBuffer = await fontResponse.arrayBuffer();
  const fontBase64 = arrayBufferToBase64(fontBuffer);
  pdfDoc.addFileToVFS(`${fontNameTTF}.ttf`, fontBase64);
  pdfDoc.addFont(`${fontNameTTF}.ttf`, fontName, fontStyle);
};

// IMAGE LOADER — profile photo needs to be base64 before jsPDF can embed it
const loadImageAsDataUrl = async (url) => {
  if (!url) return null;
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (err) {
    console.log("Failed to load image for PDF:", url, err);
    return null;
  }
};

const addHeaderFooter = (doc) => {
  const width = layout.pageWidth;
  if (config.headerimgHeight > 0) {
    doc.addImage(UnitechHeader, "PNG", 0, 0, width, config.headerimgHeight);
  }
  if (config.footerimgHeight > 0) {
    doc.addImage(UnitechFooter, "PNG", 0, layout.pageHeight - config.footerimgHeight - 3, width, config.footerimgHeight);
  }
  doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
};

const addPageNumbers = (doc, totalPages) => {
  for (let page = 1; page <= totalPages; page++) {
    doc.setPage(page);
    addHeaderFooter(doc);
    const pageNoY = layout.pageHeight - config.footerimgHeight - config.borderFooterGap;
    doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
    doc.setFontSize(config.fontSizeSm);
    doc.setTextColor(0);
    doc.text("DYC/FMT-NDT/01 Rev.00 DTD-24/05/2023", config.borderMargin, pageNoY);
    doc.text(
      `Page No : ${String(page).padStart(2, "0")} of ${String(totalPages).padStart(2, "0")}`,
      layout.pageWidth - config.margin - 80,
      pageNoY
    );
  }
};

const addTitle = () => {
  layout.doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  layout.doc.setFontSize(config.fontSizeTitle);
  layout.doc.setTextColor(0);
  layout.doc.text("CERTIFICATE OF QUALIFICATION", layout.pageWidth / 2, layout.y, { align: "center" });
  layout.advance(20);
};

// Header label/value rows (Certificate No, Date, Validity) + employee photo
const addCertHeaderBlock = (row, profileImage) => {
  const photoW = 70;
  const photoH = 85;
  const labelColW = layout.contentWidth - photoW - 10;

  autoTable(layout.doc, {
    startY: layout.y,
    margin: getTableMargins(),
    body: [
      [
        { content: "Certificate No", styles: { ...getBaseStyles(), font: customFontFamily.boldFont, fontStyle: customFontFamily.bold } },
        { content: row.certificateNo || "", styles: getBaseStyles() },
      ],
      [
        { content: "Date of Certification", styles: { ...getBaseStyles(), font: customFontFamily.boldFont, fontStyle: customFontFamily.bold } },
        { content: `${row.certificateDate ? moment(row.certificateDate).format("DD-MM-YYYY") : ""}`, styles: getBaseStyles() },
      ],
      [
        { content: "Validity of Certificate", styles: { ...getBaseStyles(), font: customFontFamily.boldFont, fontStyle: customFontFamily.bold } },
        { content: `${row.validityDate ? moment(row.validityDate).format("DD-MM-YYYY") : ""}`, styles: getBaseStyles() },
      ],
    ],
    theme: "plain",
    styles: getBaseStyles(),
    columnStyles: { 0: { cellWidth: labelColW * 0.35 }, 1: { cellWidth: labelColW * 0.65 } },
    tableWidth: labelColW,
    didDrawPage: () => { layout.y = config.contentTop; },
  });

  const boxX = config.margin + labelColW + 10;
  const boxY = layout.y;
  layout.doc.setDrawColor(0);
  layout.doc.rect(boxX, boxY, photoW, photoH);
  if (profileImage) {
    try {
      layout.doc.addImage(profileImage, "PNG", boxX + 2, boxY + 2, photoW - 4, photoH - 4);
    } catch (_) { /* skip broken image */ }
  }

  layout.y = Math.max(layout.doc.lastAutoTable.finalY, boxY + photoH) + 8;
};

// plain wrapped text (no autoTable, so there is no border at all, not even
const addCertificationParagraph = (row, employeeName) => {
  layout.doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
  layout.doc.setFontSize(config.fontSize);
  layout.doc.setTextColor(0);

  const text = row.certificateSubject || "";
  const lines = layout.doc.splitTextToSize(text, layout.contentWidth);
  layout.doc.text(lines, config.margin, layout.y);
  layout.advance(lines.length * (config.fontSize * 1.2) + 10);

  layout.doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  layout.doc.setFontSize(config.fontSizeLg);
  layout.doc.text(`MR. ${(employeeName || "").toUpperCase()}`, layout.pageWidth / 2, layout.y, { align: "center" });
  layout.advance(14);
};

const addMethodLevelTable = (row, methodLabel) => {
  autoTable(layout.doc, {
    startY: layout.y,
    margin: getTableMargins(),
    head: [[
      { content: "NDT Method", styles: {...getHeaderStyles(), halign: "center"}},
      { content: "NDT Level", styles: {...getHeaderStyles(), halign: "center"}},
      { content: "NDT Technique", styles: {...getHeaderStyles(), halign: "center"}},
      { content: "Limitations (If any)", styles: {...getHeaderStyles(), halign: "center"}},
    ]],
    body: [[methodLabel, row.ndtLevel || "", row.ndtTechnique || "N/A", row.limitations || "N/A"]],
    theme: "grid",
    styles: { ...getBaseStyles(), halign: "center", valign: "middle" },
    didDrawPage: () => { layout.y = config.contentTop; },
  });
  layout.afterTable();
};

// Now takes qualification/experience as explicit args since these live on
const addEmployeeQualRow = (employeeName, qualificationText, experienceText) => {
  const w = layout.contentWidth / 3;
  autoTable(layout.doc, {
    startY: layout.y,
    margin: getTableMargins(),
    body: [[
      { content: `MR. ${employeeName || ""}`, styles: { ...getBaseStyles(), font: customFontFamily.boldFont, fontStyle: customFontFamily.bold } },
      { content: `Qualification: ${qualificationText || ""}`, styles: getBaseStyles() },
      { content: `NDT Experience: ${experienceText || ""}`, styles: getBaseStyles() },
    ]],
    theme: "grid",
    styles: getBaseStyles(),
    columnStyles: { 0: { cellWidth: w }, 1: { cellWidth: w }, 2: { cellWidth: w } },
    didDrawPage: () => { layout.y = config.contentTop; },
  });
  layout.afterTable();
};

const addExaminationRecordTable = (row) => {
  const w = layout.contentWidth;

  autoTable(layout.doc, {
    startY: layout.y,
    margin: getTableMargins(),
    body: [
      [
        { content: `Qualification Examination Record Date of Examination: ${row.examinationDate ? moment(row.examinationDate).format("DD-MM-YYYY") : ""}`, colSpan: 5, styles: { ...getHeaderStyles() } },
      ],
      [
        { content: "General", styles: getBaseStyles() },
        { content: row.generalPercent ? `${row.generalPercent}%` : "", styles: getBaseStyles() },
        { content: "Vision Examination", colSpan: 3, styles: { ...getHeaderStyles(), halign: "center" } },
      ],
      [
        { content: "Specific", styles: getBaseStyles() },
        { content: row.specificPercent ? `${row.specificPercent}%` : "", styles: getBaseStyles() },
        { content: "Near Vision Acuity\nSatisfactory", rowSpan: 2, styles: { ...getBaseStyles(), halign: "center", valign: "middle" } },
        { content: Number(row.nearVision) === 1 ? "\u2611" : "\u2610", styles: { ...getBaseStyles(), halign: "center", valign: "middle" } },
        { content: "J1", styles: getBaseStyles() },
      ],
      [
        { content: "Practical", styles: getBaseStyles() },
        { content: row.practicalPercent ? `${row.practicalPercent}%` : "", styles: getBaseStyles() },
        { content: Number(row.nearVision) === 2 ? "\u2611" : "\u2610", styles: { ...getBaseStyles(), halign: "center", valign: "middle" } },
        { content: "J2", styles: getBaseStyles() },
      ],
      [
        { content: "Composite Score", styles: getBaseStyles() },
        { content: row.compositeScore ? `${row.compositeScore}%` : "", styles: getBaseStyles() },
        { content: "Color Contrast\nSatisfactory", rowSpan: 2, styles: { ...getBaseStyles(), halign: "center", valign: "middle" } },
        { content: Number(row.colorContrast) === 1 ? "\u2611" : "\u2610", styles: { ...getBaseStyles(), halign: "center", valign: "middle" } },
        { content: "Ishihara Plates", styles: getBaseStyles() },
      ],
      [
        { content: "Result", styles: getBaseStyles() },
        { content: row.result || "", styles: { ...getBaseStyles(),} },
        { content: Number(row.colorContrast) === 2 ? "\u2611" : "\u2610", styles: { ...getBaseStyles(), halign: "center", valign: "middle" } },
        { content: "Shades of Grey", styles: getBaseStyles() },
      ],
    ],
    theme: "grid",
    styles: getBaseStyles(),
    columnStyles: {
      0: { cellWidth: w * 0.17 },
      1: { cellWidth: w * 0.19 },
      2: { cellWidth: w * 0.28 },
      3: { cellWidth: w * 0.08 },
      4: { cellWidth: w * 0.28 },
    },
    didDrawPage: () => { layout.y = config.contentTop; },
  });
  layout.afterTable();

    autoTable(layout.doc, {
    startY: layout.y,
    margin: getTableMargins(),
    body: [
      [
        { content: "Method", styles: { ...getBaseStyles() } },
        { content: NDT_METHOD_TYPES.find((m) => m.value === row.methodType)?.label || "", styles: getBaseStyles() },
        { content: `Level : ${row.Level}`, colSpan: 2, styles: getBaseStyles() },
        { content: `Total Training Hours : ${row.totalTrainingHours ? `${row.totalTrainingHours} Hours` : "" }`, colSpan: 2, styles: { ...getBaseStyles(),} },
      ],
      [
        { content: "Previous Certificate No", colSpan: 2, styles: { ...getBaseStyles(), font: customFontFamily.boldFont, fontStyle: customFontFamily.bold } },
        { content: row.previousCertificateNo || "", colSpan: 4, styles: getBaseStyles() },
      ],
    ],
    theme: "grid",
    styles: getBaseStyles(),
    columnStyles: {
        0: { cellWidth: w * 0.17 },
        1: { cellWidth: w * 0.19 },
        2: { cellWidth: w * 0.18 },
        3: { cellWidth: w * 0.05 },
        4: { cellWidth: w * 0.08 },
        5: { cellWidth: w * 0.15 },
    },
    didDrawPage: () => { layout.y = config.contentTop; },
  });
  layout.afterTable();
};

// Now pulls text from row.validitySubject / row.recertificationProcess
const addStandardTermsBlock = (row) => {
  autoTable(layout.doc, {
    startY: layout.y,
    margin: getTableMargins(),
    body: [
      [
        { content: "The validity of this\ncertificate is subject to", styles: { ...getBaseStyles(), font: customFontFamily.boldFont, fontStyle: customFontFamily.bold } },
        { content: row.validitySubject || "", styles: getBaseStyles() },
      ],
      [
        { content: "Recertification Process", styles: { ...getBaseStyles(), font: customFontFamily.boldFont, fontStyle: customFontFamily.bold } },
        { content: row.recertificationProcess || "", styles: getBaseStyles() },
      ],
    ],
    theme: "grid",
    styles: { ...getBaseStyles(), valign: "top" },
    columnStyles: { 0: { cellWidth: layout.contentWidth * 0.25 }, 1: { cellWidth: layout.contentWidth * 0.75 } },
    didDrawPage: () => { layout.y = config.contentTop; },
  });
  layout.afterTable();
};

const addSignatureRow = () => {
  const halfW = layout.contentWidth / 2;
  autoTable(layout.doc, {
    startY: layout.y,
    margin: getTableMargins(),
    body: [
      [
        {
          content: "\n\nSignature & Stamp\nEmployer's NDT Level-III",
          styles: { ...getBaseStyles(), minCellHeight: 55, valign: "bottom" },
        },
        {
          content: "\n\nSignature\n(Certifying Authority of Employer)",
          styles: { ...getBaseStyles(), minCellHeight: 55, valign: "bottom", halign: "center" },
        },
      ],
    ],
    theme: "grid",
    styles: getBaseStyles(),
    columnStyles: { 0: { cellWidth: halfW }, 1: { cellWidth: halfW } },
    didDrawPage: () => { layout.y = config.contentTop; },
  });
  layout.afterTable();
};

const addOneCertificatePage = (row, employee, profileImage, qualificationText, experienceText) => {
  const methodLabel = NDT_METHOD_TYPES.find((m) => m.value === row.methodType)?.label || "";

  addTitle();
  addCertHeaderBlock(row, profileImage);

  layout.doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  layout.doc.setFontSize(config.fontSize);
  layout.doc.text("Certification", config.margin, layout.y);
  layout.advance(10);

  addCertificationParagraph(row, employee?.name);

  layout.doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
  layout.doc.setFontSize(config.fontSize);
  layout.doc.text(`Is hereby certified to perform the following Non-Destructive Testing (NDT) method(s):`, config.margin, layout.y);
  layout.advance(10);

  addMethodLevelTable(row, methodLabel);
  
  layout.advance(15);

  layout.doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
  layout.doc.text(
    "This company's written practice is designed to meet or exceed the requirements outlined in the ASNT document SNT-TC-1A-2020, as applicable to the Non-Destructive Testing (NDT) services conducted by the company.",
    config.margin,
    layout.y,
    { maxWidth: layout.contentWidth }
  );
  layout.advance(20);

  addEmployeeQualRow(employee?.name, qualificationText, experienceText);
  addExaminationRecordTable(row);
  addStandardTermsBlock(row);
  addSignatureRow();
};

// MAIN EXPORT — draws one certificate page per method that has data filled in.
export async function generateNdtQualificationPDF(inputStates, employee, otherData, qualification, setDownloadPdfloading) {
  try {
    setDownloadPdfloading(true);

    const rowsToPrint = (inputStates || []).filter((row) => row.certificateNo);
    // if (!rowsToPrint.length) {
    //   console.warn("No NDT method has a certificate number filled in — nothing to print.");
    //   return;
    // }

    const doc = new jsPDF({ orientation: "p", unit: "pt", format: "a4", compress: true });

    const [, , profileImage] = await Promise.all([
      loadAndAddFont(doc, AptosNormal, "Aptos", customFontFamily.normalFont, customFontFamily.normal),
      loadAndAddFont(doc, AptosBold, "Aptos-Bold", customFontFamily.boldFont, customFontFamily.bold),
      loadImageAsDataUrl(employee?.profileImageUrl),
    ]);

    doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
    layout.init(doc);

    const qualificationText = qualification?.PGName || qualification?.GraduationName || "";

    rowsToPrint.forEach((row, idx) => {
      if (idx > 0) layout.newPage();
      addOneCertificatePage(row, employee, profileImage, qualificationText, "");
    });

    addPageNumbers(doc, doc.getNumberOfPages());

    doc.save(`NDT_Certificates_${employee?.employeesCode || employee?.id || ""}.pdf`);
  } catch (error) {
    console.error("NDT certificate PDF generation error:", error);
  } finally {
    setDownloadPdfloading(false);
  }
}